import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Button from '../components/Button/Button';
import Input from '../components/Input/Input';
import Checkbox from '../components/Checkbox/Checkbox';
import ThemeToggle from '../components/ThemeToggle/ThemeToggle';
import packageInfo from '../../package.json';
import styles from './Pages.module.css';

const API_URL = '/api';
const APP_VERSION = packageInfo?.version ? `v${packageInfo.version}` : 'v1.2.1';

const AdminLoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    const from = location.state?.from?.pathname || '/map/admin/dashboard';

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        try {
            const response = await fetch(`${API_URL}/auth/check`, { 
                credentials: 'include' 
            });
            const data = await response.json();
            if (data.authenticated) {
                navigate(from, { replace: true });
            }
        } catch (err) {
            console.error('Auth check failed:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, password }),
                credentials: 'include'
            });

            const data = await response.json();

            if (response.ok && data.success) {
                navigate(from, { replace: true });
            } else {
                setError(data.message || 'Неверное имя пользователя или пароль');
            }
        } catch (err) {
            setError('Не удалось подключиться к серверу');
            console.error('Login error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.loginPage}>
            {/* Верхняя панель: ссылка на карту и переключатель темы */}
            <div className={styles.loginTopBar}>
                <Link to="/map" className={styles.loginTopLink}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    <span>Публичная карта</span>
                </Link>
                <div className={styles.loginThemeToggleWrapper}>
                    <ThemeToggle />
                </div>
            </div>

            <div className={styles.loginContainer}>
                {/* Левая часть - ультраминималистичный брендинг */}
                <div className={styles.brandSection}>
                    <div className={styles.brandGlowBackground} />
                    
                    <div className={styles.brandContentCentered}>
                        <div className={styles.loginLogoWrapper}>
                            <img src="/images/logo.png" alt="IChis SPD" className={styles.loginLogoLight} />
                            <img src="/images/logowhite.png" alt="IChis SPD" className={styles.loginLogoDark} />
                        </div>

                        <h1 className={styles.brandTitle}>IChis SPD</h1>
                    </div>

                    <div className={styles.brandVersionBottom}>
                        {APP_VERSION}
                    </div>
                </div>

                {/* Правая часть - форма входа */}
                <div className={styles.formSection}>
                    <div className={styles.formHeader}>
                        <h2>Авторизация</h2>
                        <p>Введите учетные данные для доступа</p>
                    </div>

                    <form className={styles.loginForm} onSubmit={handleSubmit}>
                        {error && (
                            <div className={styles.errorMessage}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="12" y1="8" x2="12" y2="12" />
                                    <line x1="12" y1="16" x2="12.01" y2="16" />
                                </svg>
                                <span>{error}</span>
                            </div>
                        )}

                        <div className={styles.formGroup}>
                            <Input
                                id="login-username"
                                label="Имя пользователя"
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="admin"
                                required
                                autoFocus
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <Input
                                id="login-password"
                                label="Пароль"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                            />
                        </div>

                        <div className={styles.formOptions}>
                            <Checkbox
                                checked={rememberMe}
                                onChange={setRememberMe}
                                label="Запомнить меня"
                            />
                        </div>

                        <Button
                            type="submit"
                            variant="primary"
                            fullWidth
                            size="large"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Вход...' : 'Войти'}
                        </Button>
                    </form>

                    <div className={styles.formFooter}>
                        <Link to="/map" className={styles.link}>
                            ← Вернуться на публичную карту
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminLoginPage;
